import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Clock, Lock, Mail, Play, Square, Zap } from '../../icons';
import {
  Badge, Button, EmptyState, FilterChip, SearchInput, Table, type TableColumn,
} from '../../components/shared';
import {
  listCompanyAutomations, setCompanyAutomationStatus, type CompanyAutomation,
} from '../../api/assist-automations-company';
import { WEEKDAY_LABEL } from '../../api/assist-automations';
import './CompanyAutomations.css';

/**
 * 통합설정 › 회사 관리 › AI › **업무자동화** — 회사 전체 자동화를 보고 끈다 (설계 §07).
 *
 * ## ★★★ 왜 8443 이고 관리 콘솔(8444)이 아닌가
 *
 * 위젯 관리가 여기 있는 것과 **같은 이유**다 — `CompanyAdminPanel` 의 「대시보드」 묶음 주석이
 * 그것을 적어 두었다: 「위젯을 정하는 사람은 **회사 관리자**라 관리 콘솔이 아니라 여기가 제
 * 자리다」. 자동화를 끄는 사람도 회사 관리자다. 게다가 콘솔은 **반대 방향으로 가고 있다** —
 * 회사 화면을 걷어내고 운영자 전용으로 좁히는 중이라(#1088 이 첫 삽), 거기에 회사 묶음을 새로
 * 내는 것은 그 길을 되돌리는 일이 된다.
 *
 * ## ★★★ 「상태는 배지 · 행동은 단추」 (정본 D1 의 결론)
 *
 * 상태 열은 **읽는 자리**라 배지이고, 켜고 끄는 것은 **누르는 자리**라 단추다. 하나로 합친
 * 스위치를 쓰면 「지금 켜져 있다」와 「누르면 꺼진다」가 한 칸에 겹친다.
 * ★ 그리고 정본 E1 이 못 박은 대로 — **늘 보여야 하는 행 작업(수정·중지)은 `rowActions` 가
 *   아니라 «마지막 열»**이다. 손이 닿아야 나타나는 자리에 두면 관리자가 목록을 훑다가
 *   「끌 수 있다는 사실」 자체를 못 본다.
 *
 * ## ★★ 왜 되돌릴 수 있게 두었나
 *
 * 끌 수만 있으면 실수로 끈 것을 되돌릴 길이 없다(만든 사람에게 연락이 안 닿을 수도 있다).
 * 그래서 **켜기도 같은 자리에 둔다** — 되돌리기 없는 파괴적 동작을 만들지 않는다.
 * 확인 팝업을 안 띄우는 것도 같은 까닭이다: 되돌릴 수 있는 일에 매번 묻게 하면 사람은
 * 팝업을 안 읽고 누르게 되고, 정작 돌이킬 수 없는 자리의 팝업까지 흘려 보낸다.
 *
 * ## ★ 끈 이유는 «만든 사람이 보는 자리»에 남는다
 *
 * 서버가 `pausedReason` 에 「회사 관리자가 멈췄습니다.」를 적는다 — 만든 사람의 AI › 업무자동화
 * 화면이 그 칸을 이미 그리고 있다. 새 알림 길을 내지 않아도 **왜 멈췄는지**가 전해진다.
 * 화면이 그 사실을 한 줄로 알려 주는 것은, 관리자가 「조용히 꺼진다」고 오해하지 않게 하려는 것이다.
 */

type Filter = 'all' | 'ACTIVE' | 'PAUSED';

const TRIGGER_LABEL: Record<CompanyAutomation['triggerKind'], string> = {
  SCHEDULE: '때가 되면',
  EVENT: '새 메일이 올 때',
};

/**
 * 마지막 실행 시각.
 *
 * ★ AI › 업무자동화의 같은 표시와 **모양을 맞춘다**. 거기 함수를 가져다 쓰지 않는 것은 그쪽이
 *   페이지 모듈이라, 설정 화면이 그 페이지의 의존 그래프(팝업·메일 계정 API…)를 통째로
 *   끌고 들어오기 때문이다 — 요일 이름만 같은 곳에서 가져온다.
 */
function whenText(iso: string | null): string {
  if (!iso) return '아직 없음';
  const d = new Date(iso);
  return `${d.getMonth() + 1}월 ${d.getDate()}일 (${WEEKDAY_LABEL[d.getDay()]}) ${
    String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

export function CompanyAutomationsSection(): JSX.Element {
  const [rows, setRows] = useState<CompanyAutomation[] | null>(null);
  /* ★ 상한(`limit`)은 **안 쓴다** — 잘렸는지는 `total` 하나로 말한다(줄 수와 상한을 견주면
       정확히 상한만큼 있는 회사에서 없는 절단을 알린다). */
  const [total, setTotal] = useState(0);
  const [loadError, setLoadError] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState<Filter>('all');

  const load = useCallback(async () => {
    setLoadError(false);
    try {
      const res = await listCompanyAutomations();
      setRows(res.automations);
      setTotal(res.total);
    } catch {
      setRows(null);
      setLoadError(true);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  /**
   * 상태를 바꾼다.
   *
   * ★ 다음 상태를 **부르는 쪽이 정한다** — 「멈춤↔켜짐」 토글 하나로는 「이미 멈춘 것을 잠그기」를
   *   나타낼 수 없다(둘 다 PAUSED 를 보낸다). 서버가 「멈춰 있는데 안 잠긴 것」을 잠금으로 읽는다.
   */
  const setStatus = useCallback(async (a: CompanyAutomation, next: 'ACTIVE' | 'PAUSED') => {
    setBusyId(a.id);
    setSaveError(null);
    setNote(null);
    try {
      const saved = await setCompanyAutomationStatus(a.id, next);
      setRows((cur) => (cur ?? []).map((r) => (r.id === saved.id ? saved : r)));
      setNote(next === 'PAUSED'
        ? `「${a.name}」 을(를) 잠갔습니다 — 만든 사람(${a.ownerName})에게는 «내가 멈췄다»고 보이며, `
          + '그분은 다시 켤 수 없습니다.'
        : `「${a.name}」 을(를) 다시 켰습니다 — 잠금도 함께 풀렸습니다.`);
    } catch (e) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setSaveError(msg ?? '바꾸지 못했습니다. 잠시 후 다시 시도해 주세요.');
      /* 서버가 막았을 수도 있고 그 사이 누가 지웠을 수도 있다 — 화면을 서버 것으로 되돌린다. */
      void load();
    } finally {
      setBusyId(null);
    }
  }, [load]);

  const all = rows ?? [];
  const activeCount = all.filter((a) => a.status === 'ACTIVE').length;
  const pausedCount = all.length - activeCount;

  /** 이름과 만든 사람을 함께 본다 — 「누가 만든 것들인가」로 찾는 일이 잦다. */
  const needle = q.trim().toLowerCase();
  const shown = useMemo(() => all.filter((a) => {
    if (filter !== 'all' && a.status !== filter) return false;
    if (!needle) return true;
    return `${a.name} ${a.ownerName}`.toLowerCase().includes(needle);
  }), [all, filter, needle]);

  const columns: ReadonlyArray<TableColumn<CompanyAutomation>> = [
    {
      key: 'name', header: '이름', rowHeader: true,
      render: (a) => (
        <div className="cauto__name">
          <span className="cauto__nm">{a.name}</span>
          {/* 멈춘 까닭은 이름 밑에 — 상태 배지만으로는 「왜」를 못 적는다. */}
          {a.status !== 'ACTIVE' && (a.adminHeld || a.pausedReason !== null) && (
            <span className="cauto__why">
              {a.adminHeld
                ? (a.pausedByName !== null ? `${a.pausedByName} 님이 잠갔습니다` : '회사 관리자가 잠갔습니다')
                : a.pausedReason}
            </span>
          )}
        </div>
      ),
    },
    {
      key: 'triggerKind', header: '언제 도나', width: '150px',
      render: (a) => (
        <span className="cauto__trig">
          {a.triggerKind === 'EVENT' ? <Mail size={13} aria-hidden /> : <Clock size={13} aria-hidden />}
          {TRIGGER_LABEL[a.triggerKind]}
        </span>
      ),
    },
    {
      key: 'ownerName', header: '만든 사람', width: '160px',
      render: (a) => (
        <span className="cauto__owner">
          {a.ownerName}
          {/* ★★★ 비활성 계정의 자동화는 **돌지 않는데도 멀쩡해 보인다**(건너뛴 회차도 마지막
              실행을 올린다). 그래서 「켜짐」 옆에 이 사실을 적는다 — 안 적으면 아무도
              이상하다고 생각할 이유가 없다. */}
          {!a.ownerActive && <Badge variant="warning" size="sm">비활성</Badge>}
        </span>
      ),
    },
    {
      key: 'lastRunAt', header: '마지막 실행', width: '180px',
      render: (a) => whenText(a.lastRunAt),
    },
    {
      key: 'status', header: '상태', width: '150px',
      render: (a) => (
        <span className="cauto__st">
          {a.status === 'ACTIVE'
            ? <Badge variant="info" size="sm">켜짐</Badge>
            : <Badge variant="warning" size="sm">멈춤</Badge>}
          {/* ★★ 「멈춤」과 「관리자가 잠금」은 다른 상태다 — 잠긴 것만 만든 사람이 못 켠다. */}
          {a.adminHeld && <Badge variant="danger" size="sm">잠김</Badge>}
          {/* 연속 실패는 «아직 켜져 있어도» 알아야 한다 — 5가 되면 서버가 스스로 멈춘다. */}
          {a.failStreak > 0 && (
            <Badge variant="danger" size="sm">연속 실패 {a.failStreak}</Badge>
          )}
        </span>
      ),
    },
    {
      /* ★ 정본 E1 — 늘 보여야 하는 행 작업은 `rowActions` 가 아니라 마지막 «열»이다. */
      key: 'act', header: '', width: '112px', align: 'right',
      render: (a) => (
        <span className="cauto__rowacts">
          {/*
            * ★★★ **「멈춰 있다」와 「잠겼다」는 다른 상태다.** 연속 실패로 서버가 멈춘 것은
            *   만든 사람이 그냥 되켤 수 있어서, 관리자가 그것을 «잠글» 길이 있어야 한다.
            *   그래서 멈춰 있는데 안 잠긴 줄에는 **「잠그기」**가 선다.
            */}
          {a.status !== 'ACTIVE' && !a.adminHeld && (
            <Button
              variant="secondary" size="sm" disabled={busyId !== null}
              iconLeft={<Lock size={13} />}
              onClick={() => { void setStatus(a, 'PAUSED'); }}
            >
              잠그기
            </Button>
          )}
          <Button
            variant="secondary"
            size="sm"
            disabled={busyId !== null}
            iconLeft={a.status === 'ACTIVE' ? <Square size={13} /> : <Play size={13} />}
            onClick={() => { void setStatus(a, a.status === 'ACTIVE' ? 'PAUSED' : 'ACTIVE'); }}
          >
            {a.status === 'ACTIVE' ? '멈춤' : '다시 시작'}
          </Button>
        </span>
      ),
    },
  ];

  if (loadError) {
    return (
      <EmptyState
        icon={AlertTriangle}
        title="목록을 불러오지 못했습니다"
        description="잠시 후 다시 시도해 주세요."
        action={<Button variant="secondary" onClick={() => void load()}>다시 시도</Button>}
      />
    );
  }

  return (
    <div className="cauto">
      <p className="cauto__hint">
        회사에서 돌고 있는 <b>업무자동화 전부</b>입니다. 여기서 할 수 있는 것은 <b>멈추기와 다시
        켜기</b>뿐이며, 이름·묻는 말·도는 때는 <b>만든 사람만</b> 바꿀 수 있습니다.
        <br />
        멈추면 만든 사람 화면에 <b>누가 멈췄는지</b>가 적히고, <b>만든 사람은 다시 켤 수
        없습니다</b> — 켜는 것도 여기서 합니다. 연속 실패로 이미 멈춰 있는 것도 <b>잠글</b> 수 있습니다.
      </p>

      {saveError !== null && (
        <div className="cauto__alert" role="alert">
          <AlertTriangle size={16} aria-hidden />
          <span>{saveError}</span>
        </div>
      )}
      {note !== null && <p className="cauto__note" role="status">{note}</p>}

      <div className="cauto__bar">
        <SearchInput
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onClear={() => setQ('')}
          placeholder="이름 · 만든 사람 검색"
          aria-label="자동화 검색"
        />
        <div className="cauto__chips">
          <FilterChip pressed={filter === 'all'} onClick={() => setFilter('all')}>
            전체 {all.length}
          </FilterChip>
          <FilterChip pressed={filter === 'ACTIVE'} onClick={() => setFilter('ACTIVE')}>
            켜짐 {activeCount}
          </FilterChip>
          <FilterChip pressed={filter === 'PAUSED'} onClick={() => setFilter('PAUSED')}>
            멈춤 {pausedCount}
          </FilterChip>
        </div>
      </div>

      <Table<CompanyAutomation>
        caption="회사 업무자동화"
        columns={columns}
        rows={shown}
        rowKey={(a) => a.id}
        loading={rows === null}
        wrapClassName="cauto__wrap"
        rowClassName={(a) => (a.status === 'ACTIVE' ? undefined : 'is-stopped')}
        empty={(
          <EmptyState
            icon={Zap}
            title={all.length === 0 ? '아직 만들어진 자동화가 없습니다' : '찾는 자동화가 없습니다'}
            description={all.length === 0
              ? '직원이 AI 메뉴의 업무자동화에서 만들면 여기에 나타납니다.'
              : '검색어나 상태 거르개를 바꿔 보세요.'}
          />
        )}
      />

      {/*
        * ⚠⚠⚠ 처음에 「가장 최근 200개 · 회사 한도와 같은 수라 더 못 만든다」고 적었는데 **둘 다
        *   틀린 말**이었다 — 차례는 최근순이 아니고(켜진 것이 먼저다), 회사 한도는 «켜진 것»만
        *   세므로 전체 줄 수는 얼마든지 그보다 많을 수 있다.
        * ★ 잘렸는지는 **서버가 준 `total`** 로만 알 수 있다. 줄 수로 짐작하면 정확히 상한만큼
        *   있는 회사에서 없는 절단을 알린다.
        */}
      {total > all.length && (
        <p className="cauto__foot">
          전체 <b>{total}</b>개 중 <b>{all.length}</b>개를 보여 줍니다 — <b>켜진 것부터</b>
          {' '}싣기 때문에, 빠진 것은 이미 멈춰 있는 자동화입니다.
        </p>
      )}
    </div>
  );
}
